import { useState } from "react";

const checkIsIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const checkIsAndroid = () => /Android/i.test(navigator.userAgent);

function waitForConfigurator(timeoutMs: number): Promise<any | null> {
  return new Promise((resolve) => {
    if ((window as any).configurator) return resolve((window as any).configurator);
    const start = Date.now();
    const id = setInterval(() => {
      if ((window as any).configurator) {
        clearInterval(id);
        resolve((window as any).configurator);
      } else if (Date.now() - start > timeoutMs) {
        clearInterval(id);
        resolve(null);
      }
    }, 200);
  });
}

export type ArStatus =
  | "idle"
  | "waiting"     // waiting for configurator
  | "exporting"   // exporting 3D model from PlayCanvas
  | "optimizing"  // uploading to server + optimizing
  | "done"
  | "error";

export type UseArExportReturn = {
  isLoading: boolean;
  isMobile: boolean;
  isIOS: boolean;
  qrValue: string;
  iosArUrl: string;
  status: ArStatus;
  trigger: () => Promise<void>;
};

export function useArExport(): UseArExportReturn {
  const ios = checkIsIOS();
  const android = checkIsAndroid();
  const isMobile = ios || android;

  // Start with "waiting" on desktop to avoid flash of error state
  const [status, setStatus] = useState<ArStatus>(!isMobile ? "waiting" : "idle");
  const [qrValue, setQrValue] = useState("");
  const [iosArUrl, setIosArUrl] = useState("");

  const isLoading = status !== "idle" && status !== "done" && status !== "error";

  const trigger = async () => {
    setStatus("waiting");
    setQrValue("");
    setIosArUrl("");

    try {
      const configurator = await waitForConfigurator(10000);
      if (!configurator) {
        throw new Error("[AR] window.configurator not ready after 10s");
      }

      const totalStart = performance.now();

      // Step 1: Export models from PlayCanvas
      setStatus("exporting");
      const [glb, usdz]: [Blob, Blob] = await Promise.all([
        configurator.exportGLBAsBlob(),
        configurator.exportUSDZAsBlob(),
      ]);
      console.log(`[AR] PlayCanvas export: ${((performance.now() - totalStart) / 1000).toFixed(2)}s (GLB: ${(glb.size / 1024 / 1024).toFixed(2)} MB, USDZ: ${(usdz.size / 1024 / 1024).toFixed(2)} MB)`);

      // iOS: set blob URL immediately so button appears without waiting for server
      if (ios) {
        setIosArUrl(URL.createObjectURL(usdz));
      }

      // Step 2: Upload to server for optimization + get QR id
      setStatus("optimizing");
      const timestamp = Date.now();
      const formData = new FormData();
      formData.append("configuration", JSON.stringify({ data: "test" }));
      formData.append("glb", new File([glb], `ar_${timestamp}.glb`, { type: "model/gltf-binary" }));
      formData.append("usdz", new File([usdz], `ar_${timestamp}.usdz`, { type: "model/vnd.usdz+zip" }));

      const uploadStart = performance.now();
      const response = await fetch("/api/ar/upload", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error(`[AR] Upload failed: ${response.status}`);

      const data = await response.json() as { id?: number };
      console.log(`[AR] Server upload + optimize: ${((performance.now() - uploadStart) / 1000).toFixed(2)}s`);
      console.log(`[AR] Total: ${((performance.now() - totalStart) / 1000).toFixed(2)}s`, data);

      const configId = data?.id;
      if (configId) {
        setQrValue(`${window.location.origin}/ar/view/${configId}`);
      }

      setStatus("done");
    } catch (err) {
      console.error("[AR] Export failed", err);
      // If iOS already has blob URL, don't show error — AR button is already available
      if (ios && document.querySelector('a[rel="ar"]')) {
        setStatus("done");
      } else {
        setStatus("error");
      }
    }
  };

  return { isLoading, isMobile, isIOS: ios, qrValue, iosArUrl, status, trigger };
}
